<template>
  <div>
    <!-- Install Banner -->
    <v-snackbar
      v-model="showInstallBanner"
      :timeout="-1"
      bottom
      left
      color="primary"
      class="pwa-install-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon left color="white">mdi-download</v-icon>
        <span>{{ $t('pwa.installPrompt') || 'Instaliraj Firmwide aplikaciju' }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="installApp" color="white">
          {{ $t('pwa.install') || 'Instaliraj' }}
        </v-btn>
        <v-btn icon v-bind="attrs" @click="dismissInstall">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Update Available Banner -->
    <v-snackbar
      v-model="showUpdateBanner"
      :timeout="-1"
      top
      color="info"
      class="pwa-update-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon left color="white">mdi-update</v-icon>
        <span>{{ $t('pwa.updateAvailable') || 'Nova verzija je dostupna' }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="applyUpdate" color="white">
          {{ $t('pwa.update') || 'Ažuriraj' }}
        </v-btn>
        <v-btn icon v-bind="attrs" @click="showUpdateBanner = false">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Offline Banner -->
    <v-snackbar
      v-model="showOfflineBanner"
      :timeout="3000"
      bottom
      color="warning"
    >
      <div class="d-flex align-center">
        <v-icon left color="white">mdi-wifi-off</v-icon>
        <span>{{ $t('pwa.offline') || 'Radite u offline načinu' }}</span>
      </div>
    </v-snackbar>

    <!-- Notification Permission Dialog -->
    <v-dialog v-model="showNotificationDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-bell</v-icon>
          {{ $t('pwa.notificationTitle') || 'Omogući obavijesti' }}
        </v-card-title>
        <v-card-text>
          {{ $t('pwa.notificationMessage') || 'Omogućite obavijesti kako biste primali obavijesti o novim zadaćama, rokovima i porukama.' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="denyNotifications">
            {{ $t('pwa.later') || 'Kasnije' }}
          </v-btn>
          <v-btn color="primary" @click="allowNotifications">
            {{ $t('pwa.allow') || 'Dopusti' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'PWAInstallPrompt',

  data() {
    return {
      showInstallBanner: false,
      showUpdateBanner: false,
      showOfflineBanner: false,
      showNotificationDialog: false,
      installDismissed: false,
      notificationPromptShown: false
    };
  },

  computed: {
    pwaState() {
      return this.$store.state.pwa || {};
    }
  },

  watch: {
    'pwaState.canInstall': {
      handler(canInstall) {
        if (canInstall && !this.installDismissed) {
          // Show install banner after a delay
          setTimeout(() => {
            this.showInstallBanner = true;
          }, 5000);
        }
      },
      immediate: true
    },

    'pwaState.updateAvailable': {
      handler(updateAvailable) {
        if (updateAvailable) {
          this.showUpdateBanner = true;
        }
      },
      immediate: true
    },

    'pwaState.isOnline': {
      handler(isOnline, wasOnline) {
        if (wasOnline !== undefined && !isOnline) {
          this.showOfflineBanner = true;
        }
      }
    }
  },

  mounted() {
    // Check if we should show notification permission prompt
    this.checkNotificationPermission();
  },

  methods: {
    async installApp() {
      const installed = await this.$pwa.promptInstall();
      if (installed) {
        this.showInstallBanner = false;
        // Show notification permission dialog after install
        this.checkNotificationPermission();
      }
    },

    dismissInstall() {
      this.showInstallBanner = false;
      this.installDismissed = true;
      // Store in localStorage to not show again for a while
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    },

    applyUpdate() {
      this.$pwa.applyUpdate();
    },

    checkNotificationPermission() {
      // Only show if notifications are supported and permission is default
      if (
        this.$notifications.isSupported() &&
        this.$notifications.getPermission() === 'default' &&
        !this.notificationPromptShown
      ) {
        // Show dialog after user has been using the app for a bit
        const lastPrompt = localStorage.getItem('notification-prompt-time');
        const now = Date.now();

        // Don't show more than once per day
        if (!lastPrompt || now - parseInt(lastPrompt) > 24 * 60 * 60 * 1000) {
          setTimeout(() => {
            this.showNotificationDialog = true;
            this.notificationPromptShown = true;
          }, 10000);
        }
      }
    },

    async allowNotifications() {
      const permission = await this.$notifications.requestPermission();
      this.showNotificationDialog = false;
      localStorage.setItem('notification-prompt-time', Date.now().toString());

      if (permission === 'granted') {
        // Show a test notification
        this.$notifications.show('Obavijesti omogućene', {
          body: 'Sada ćete primati obavijesti iz Firmwide sustava.',
          icon: '/android-icon-192x192.png'
        });
      }
    },

    denyNotifications() {
      this.showNotificationDialog = false;
      localStorage.setItem('notification-prompt-time', Date.now().toString());
    }
  }
};
</script>

<style scoped>
.pwa-install-snackbar :deep(.v-snack__wrapper) {
  min-width: 300px;
}

.pwa-update-snackbar :deep(.v-snack__wrapper) {
  min-width: 300px;
}
</style>
